import type React from "react";

import { startCase } from "lodash";

import { CryptoCombobox } from "../presentation/CryptoCombobox";
import { ConfirmWithdraw } from "./presentation/ConfirmWithdraw";
import { Deposit } from "./presentation/Deposit";
import { TabButton } from "./presentation/TabButton";
import { Withdraw } from "./presentation/Withdraw";
import { WithdrawError } from "./presentation/WithdrawError";
import type { Tab } from "./schema";
import type { TransferCardControllerReturn } from "./TransferCard.controller";

export interface TransferCardPresentationProps
  extends TransferCardControllerReturn {}

const TAB_IDS: Record<Tab, { tabId: string; panelId: string }> = {
  deposit: { tabId: "transfer-tab-deposit", panelId: "transfer-panel-deposit" },
  withdraw: {
    tabId: "transfer-tab-withdraw",
    panelId: "transfer-panel-withdraw",
  },
};

const TABS: Tab[] = ["deposit", "withdraw"];

export const TransferCardPresentation = ({
  tab,
  onTabChanged,
  chain,
  chainOptions,
  onChainChanged,
  assetCode,
  assetError,
  assetOptions,
  isAssetDisabled,
  asset,
  selectedAssetDisplay,
  onAssetChanged,
  onAddWhitelistAddressClicked,
  fee,
  withdraw,
  deposit,
}: TransferCardPresentationProps) => {
  // Accessible tab navigation
  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = TABS.indexOf(tab);
    let nextIndex = -1;
    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % TABS.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    }
    if (nextIndex >= 0) {
      e.preventDefault();
      onTabChanged(TABS[nextIndex]);
    }
  };

  return (
    <form
      className="flex flex-col gap-4 rounded border border-gray-200 p-4"
      onSubmit={(event) => event.preventDefault()}
    >
      {tab === "withdraw" && withdraw.error ? (
        <WithdrawError withdraw={withdraw} />
      ) : tab === "withdraw" && withdraw.isConfirming ? (
        <ConfirmWithdraw
          asset={asset}
          fee={fee}
          withdraw={withdraw}
          assetCode={assetCode}
          selectedAssetDisplay={selectedAssetDisplay}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <h2 className="sr-only">Crypto Transfer {startCase(tab)}</h2>
          <div
            role="tablist"
            aria-label="Transfer type"
            className="flex items-center justify-start gap-2"
            onKeyDown={handleTabKeyDown}
          >
            <TabButton
              id={TAB_IDS.deposit.tabId}
              panelId={TAB_IDS.deposit.panelId}
              label="Deposit"
              isSelected={tab === "deposit"}
              onClick={() => onTabChanged("deposit")}
            />
            <TabButton
              id={TAB_IDS.withdraw.tabId}
              panelId={TAB_IDS.withdraw.panelId}
              label="Withdraw"
              isSelected={tab === "withdraw"}
              onClick={() => onTabChanged("withdraw")}
            />
          </div>

          <div
            role="tabpanel"
            id={TAB_IDS[tab].panelId}
            aria-labelledby={TAB_IDS[tab].tabId}
            className="flex flex-col gap-3"
          >
            <CryptoCombobox
              label="Select Chain"
              placeholder="Select a chain"
              options={chainOptions}
              value={chain}
              onChange={onChainChanged}
              className="z-30 w-full"
            />

            <CryptoCombobox
              label="Select Asset"
              placeholder="Select an asset"
              options={assetOptions}
              value={assetCode}
              onChange={onAssetChanged}
              disabled={isAssetDisabled}
              errorMessage={assetError ?? undefined}
              className="z-20 w-full"
            />

            {tab === "deposit" && <Deposit asset={asset} deposit={deposit} />}

            {tab === "withdraw" && (
              <Withdraw
                asset={asset}
                withdraw={withdraw}
                fee={fee}
                assetCode={assetCode === "NATIVE" ? chain : assetCode}
                onAddWhitelistAddressClicked={onAddWhitelistAddressClicked}
              />
            )}
          </div>
        </div>
      )}
    </form>
  );
};
